import { useEffect, useState } from "react";

export interface RecentlyViewedCompany {
	ico: string;
	name: string;
	city: string | null;
	viewedAt: number;
}

const STORAGE_KEY = "firmindex:recently-viewed";
const MAX_ENTRIES = 8;

function readEntries(): RecentlyViewedCompany[] {
	if (typeof window === "undefined") return [];
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		if (!raw) return [];
		const parsed = JSON.parse(raw);
		if (!Array.isArray(parsed)) return [];
		return parsed.filter(
			(e): e is RecentlyViewedCompany =>
				typeof e === "object" &&
				e !== null &&
				typeof e.ico === "string" &&
				typeof e.name === "string",
		);
	} catch {
		return [];
	}
}

/** Records a company profile visit — call from the company layout (client only). */
export function recordCompanyVisit(company: { ico: string; name: string; city?: string | null }) {
	if (typeof window === "undefined") return;
	try {
		const entries = readEntries().filter((e) => e.ico !== company.ico);
		entries.unshift({
			ico: company.ico,
			name: company.name,
			city: company.city ?? null,
			viewedAt: Date.now(),
		});
		window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(0, MAX_ENTRIES)));
	} catch {
		// localStorage unavailable (private mode, quota) — recently viewed is non-critical
	}
}

/** Recently viewed companies, newest first. Empty during SSR and first render. */
export function useRecentlyViewed(): RecentlyViewedCompany[] {
	const [entries, setEntries] = useState<RecentlyViewedCompany[]>([]);

	useEffect(() => {
		setEntries(readEntries());
	}, []);

	return entries;
}
